import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Aggregate handover progress, safe for every estate member: counts only. The API never sends
 * item names, notes, owners or documents here, so there is nothing to hide in the markup.
 * Renders nothing when no handover is being tracked (or the viewer may not see it).
 */
export default function HandoverProgress({ isAdmin = false }) {
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get("/handover/summary");
                if (alive) setSummary(res.data);
            } catch {
                if (alive) setSummary(null); // not permitted, or unavailable: show nothing
            }
        })();
        return () => { alive = false; };
    }, []);

    if (!summary || !summary.active) return null;
    const { counts } = summary;

    return (
        <section className="mt-8" data-testid="handover-progress">
            <h2 className="font-heading text-lg font-semibold">Handover from the previous managing agent</h2>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">{summary.note}</p>
            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-heading text-2xl font-bold tabular-nums" data-testid="handover-progress-headline">
                        {summary.accepted} of {summary.applicable}
                    </div>
                    <div className="text-sm text-slate-600">
                        records accepted{summary.outgoing_agency ? ` · from ${summary.outgoing_agency}` : ""}
                    </div>
                </div>
                <div
                    className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={summary.percent_accepted}
                    aria-label="Handover records accepted"
                >
                    <div className="h-full bg-slate-700" style={{ width: `${summary.percent_accepted}%` }} />
                </div>
                <p className="text-xs text-slate-600 mt-3" data-testid="handover-progress-counts">
                    {counts.requested} asked for · {counts.received} received · {counts.under_review} under review
                    {counts.needs_correction ? ` · ${counts.needs_correction} need correction` : ""}
                    {counts.not_requested ? ` · ${counts.not_requested} not yet asked for` : ""}
                </p>
                {isAdmin && (
                    <Link to="/handover" className="text-xs underline text-slate-700 mt-2 inline-block">
                        Open the handover tracker
                    </Link>
                )}
            </div>
        </section>
    );
}
